import type { BlowoutWeek, PayoutAward, PayoutWinner, TeamRecord } from "@averagejoesff/shared";
import { TeamManagerLabel } from "../../components/TeamManagerLabel";
import { formatCurrency } from "../../lib/format";

interface PayoutAwardListProps {
  awards: PayoutAward[];
  note?: string;
  /** This season's manually-assigned winners (see `SeasonPayoutWinners`). */
  winners: PayoutWinner[];
  /** This season's weekly blowout results, for the `"blowoutWeekly"` category. */
  weeks: BlowoutWeek[];
  /** This season's regular-season standings, for the `"seasonStandings"` category. */
  standings: TeamRecord[];
  labelByTeam: Map<string, string>;
}

type PlayedWeek = BlowoutWeek & { winner: NonNullable<BlowoutWeek["winner"]> };

function hasWinner(week: BlowoutWeek): week is PlayedWeek {
  return week.status === "final" && week.winner != null;
}

const PLACE_ORDER = ["1st", "2nd", "3rd"] as const;
const PLACE_PATTERN = /\b(1st|2nd|3rd)\s+place\b/i;
const PLACE_MEDALS = ["🥇", "🥈", "🥉"] as const;
const PLACE_TIERS = ["gold", "silver", "bronze"] as const;

/**
 * A category's 0/1/2 podium position when its label names a place (e.g.
 * "Playoffs 3rd place"), or `null` otherwise. Dollar amount alone can't
 * reliably tell a "3rd place" category apart from an unrelated one worth
 * the same amount (they're frequently tied — e.g. "Best regular season
 * standing" and "Playoffs 3rd place" are both $50 in this league's
 * structure), so podium order comes from the label itself — the same
 * wording admins already use to name these categories.
 */
function placeRank(label: string): number | null {
  const match = label.match(PLACE_PATTERN);
  if (!match) return null;
  const index = PLACE_ORDER.indexOf(match[1]!.toLowerCase() as (typeof PLACE_ORDER)[number]);
  return index === -1 ? null : index;
}

/** Medal for a podium place, a crown for the computed standings leader
 * (distinct from "3rd place" even when they're worth the same amount),
 * a plain trophy for everything else. */
function iconFor(award: PayoutAward, rank: number | null): string {
  if (rank !== null) return PLACE_MEDALS[rank]!;
  if (award.source === "seasonStandings") return "👑";
  return "🏆";
}

interface TrophyCardProps {
  award: PayoutAward;
  winnerName: string | undefined;
  labelByTeam: Map<string, string>;
  rank: number | null;
  /** Only for the `"blowoutWeekly"` category — every week with a decided result. */
  playedWeeks?: PlayedWeek[];
}

/** The one category with no single winner: headline the count instead of a
 * team name, and let the week-by-week breakdown expand right in the card,
 * in the same spot a manager label would go for every other category. */
function BlowoutCardBody({ award, playedWeeks, labelByTeam }: TrophyCardProps) {
  const count = playedWeeks?.length ?? 0;

  if (count === 0) {
    return (
      <span className="trophy-card__winner trophy-card__winner--empty">No weeks played yet</span>
    );
  }

  return (
    <>
      <span className="trophy-card__winner">
        {count} Weekly Winner{count === 1 ? "" : "s"}
      </span>
      <details className="trophy-card__weekly">
        <summary>Week-by-week</summary>
        <ol className="payout-awards__weekly-list">
          {playedWeeks!.map((week) => (
            <li key={week.week} className="payout-awards__weekly-row">
              Wk {week.week}: <strong>{week.winner.name}</strong>
              <TeamManagerLabel label={labelByTeam.get(week.winner.name)} />
            </li>
          ))}
        </ol>
      </details>
      <span className="trophy-card__label">
        {award.label}
        <span className="payout-awards__auto-badge">Auto</span>
      </span>
      <span className="trophy-card__amount">{formatCurrency(award.amount)}</span>
    </>
  );
}

function TrophyCard(props: TrophyCardProps) {
  const { award, winnerName, labelByTeam, rank } = props;

  if (award.source === "blowoutWeekly") {
    const empty = (props.playedWeeks?.length ?? 0) === 0;
    return (
      <li className={`trophy-card${empty ? " trophy-card--empty" : ""}`}>
        <span className="trophy-card__medal" aria-hidden="true">
          💥
        </span>
        <BlowoutCardBody {...props} />
      </li>
    );
  }

  const tier = rank !== null ? PLACE_TIERS[rank] : null;
  const tierClass = tier ? ` trophy-card--${tier}` : "";

  return (
    <li className={`trophy-card${winnerName ? tierClass : " trophy-card--empty"}`}>
      <span className="trophy-card__medal" aria-hidden="true">
        {winnerName ? iconFor(award, rank) : "❔"}
      </span>
      {winnerName ? (
        <>
          <span className="trophy-card__winner">{winnerName}</span>
          <TeamManagerLabel label={labelByTeam.get(winnerName)} />
        </>
      ) : (
        <span className="trophy-card__winner trophy-card__winner--empty">Not yet awarded</span>
      )}
      <span className="trophy-card__label">
        {award.label}
        {award.source === "seasonStandings" && <span className="payout-awards__auto-badge">Auto</span>}
      </span>
      <span className="trophy-card__amount">{formatCurrency(award.amount)}</span>
    </li>
  );
}

/**
 * Winners take center stage: every award category is a "trophy card" —
 * even the weekly blowout, which has no single season winner, headlines its
 * count instead and expands to the week-by-week breakdown right in the
 * card. The podium categories (1st/2nd/3rd place) lead in their own row, in
 * place order; everything else follows in a second row. Both rows wrap and
 * center themselves (`.trophy-row`), so a short second row sits centered
 * under the first rather than hugging the left edge.
 */
export function PayoutAwardList({
  awards,
  note,
  winners,
  weeks,
  standings,
  labelByTeam,
}: PayoutAwardListProps) {
  if (awards.length === 0) {
    return <p className="payout-awards__empty">No awards configured yet.</p>;
  }

  const winnerByLabel = new Map(winners.map((w) => [w.awardLabel, w.teamName]));
  const playedWeeks = weeks.filter(hasWinner);

  const podiumAwards = awards
    .filter((a) => placeRank(a.label) !== null)
    .sort((a, b) => placeRank(a.label)! - placeRank(b.label)!);
  // The weekly blowout card is the only one that expands to a long list —
  // keep it last so opening it never pushes other cards down out of place.
  const extraAwards = awards
    .filter((a) => placeRank(a.label) === null)
    .sort((a, b) => Number(a.source === "blowoutWeekly") - Number(b.source === "blowoutWeekly"));

  function winnerNameFor(award: PayoutAward): string | undefined {
    if (award.source === "seasonStandings") return standings[0]?.name;
    return winnerByLabel.get(award.label);
  }

  function trophyRow(rowAwards: PayoutAward[]) {
    return (
      <ul className="trophy-row">
        {rowAwards.map((award, i) => (
          <TrophyCard
            key={i}
            award={award}
            winnerName={winnerNameFor(award)}
            labelByTeam={labelByTeam}
            rank={placeRank(award.label)}
            playedWeeks={award.source === "blowoutWeekly" ? playedWeeks : undefined}
          />
        ))}
      </ul>
    );
  }

  return (
    <>
      {podiumAwards.length > 0 && trophyRow(podiumAwards)}
      {extraAwards.length > 0 && trophyRow(extraAwards)}
      {note && <p className="payout-awards__note">{note}</p>}
    </>
  );
}
